import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  LuCircleCheck, LuCircleX, LuClock, LuUser, LuDatabase, 
  LuActivity, LuShieldCheck, LuDownload, LuGift, LuTicket,
  LuHourglass, LuSparkles, LuArrowRight, LuLifeBuoy, LuUsers,
  LuPlus, LuWallet, LuCreditCard
} from 'react-icons/lu';
import { FaWindows, FaAndroid, FaApple } from 'react-icons/fa';

// Кэш курсов валют
let currencyRatesCache = {
  UAH: 40.0,
  RUB: 100.0,
  USD: 1.0
};

// Загружаем курсы валют из API
const loadCurrencyRates = async () => {
  try {
    const response = await fetch('/api/public/currency-rates');
    if (response.ok) {
      const data = await response.json();
      currencyRatesCache = { ...currencyRatesCache, ...data.rates };
    }
  } catch (e) {
    console.error('Ошибка загрузки курсов валют:', e);
  }
};

// Загружаем курсы при инициализации
loadCurrencyRates();

// Функция конвертации баланса из USD в выбранную валюту
const convertBalanceFromUSD = (balanceUSD, targetCurrency) => {
  if (!balanceUSD || balanceUSD === 0) return 0;
  const currency = (targetCurrency || 'usd').toUpperCase();
  const rate = currencyRatesCache[currency] || 1.0;
  return balanceUSD * rate;
};

// --- Виджет: Триал ---
const TrialCard = ({ onActivateTrial, loading, error }) => {
  const { t, i18n } = useTranslation();
  const [trialSettings, setTrialSettings] = useState(null);
  
  // Загружаем настройки триала
  useEffect(() => {
    const fetchTrialSettings = async () => {
      try {
        const response = await fetch('/api/public/trial-settings');
        if (response.ok) {
          const data = await response.json();
          setTrialSettings(data);
        }
      } catch (e) {
        console.error('Ошибка загрузки настроек триала:', e);
      }
    };
    fetchTrialSettings();
  }, []);
  
  // Определяем язык для получения текста
  const lang = i18n.language || 'ru';
  const langKey = lang === 'ua' ? 'ua' : lang === 'en' ? 'en' : lang === 'cn' ? 'cn' : 'ru';
  
  // Получаем тексты из настроек или используем дефолтные
  const title = trialSettings ? (trialSettings[`title_${langKey}`] || trialSettings.title_ru || t('trial.hero_title', 'Получите 3 дня премиум')) : t('trial.hero_title', 'Получите 3 дня премиум');
  const description = trialSettings ? (trialSettings[`description_${langKey}`] || trialSettings.description_ru || t('trial.cta_desc', 'Дадим полный доступ без ограничений — протестируйте сеть перед оплатой.')) : t('trial.cta_desc', 'Дадим полный доступ без ограничений — протестируйте сеть перед оплатой.');
  const buttonText = trialSettings ? (trialSettings[`button_text_${langKey}`] || trialSettings.button_text_ru || t('trial.cta', '🎁 Попробовать бесплатно')) : t('trial.cta', '🎁 Попробовать бесплатно');
  
  // Если триал отключен, не показываем карточку
  if (trialSettings && !trialSettings.enabled) {
    return null;
  }
  
  return (
    <div className="glass-card trial-card gradient-card">
      <div className="trial-icon-wrapper"><LuGift /></div>
      <div className="trial-content">
        <p className="eyebrow-text">{t('trial.no_subscription')}</p>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="trial-actions">
        <button className="btn btn-trial" onClick={onActivateTrial} disabled={loading}>
          {loading ? t('trial.activating') : buttonText}
        </button>
      </div>
      {error && <div className="message-error" style={{marginTop: '15px'}}>{error}</div>}
    </div>
  );
};

// --- Виджет: Промокод ---
const PromoCodeWidget = ({ onUpdateUser }) => {
  const { token } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleActivate = async (e) => {
    e.preventDefault();
    if(!code.trim()) return;
    
    setLoading(true); setStatus(null);
    try {
      const response = await fetch('/api/client/activate-promocode', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ promo_code: code })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      
      setStatus({ type: 'success', msg: data.message });
      setCode('');
      
      setTimeout(() => window.location.reload(), 1500); 
      
    } catch (err) {
      setStatus({ type: 'error', msg: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card info-card softer-card promo-card">
      <h3 className="client-widget-title"><LuTicket style={{marginRight: 8}}/> Активация промокода</h3>
      <form onSubmit={handleActivate} className="promo-form">
        <div className="input-with-button">
          <input 
            type="text" 
            placeholder="Введите код..." 
            value={code} 
            onChange={e => setCode(e.target.value)} 
            disabled={loading} 
          />
          <button className="btn-copy" type="submit" disabled={loading}>
            {loading ? '...' : 'OK'}
          </button>
        </div>
        {status && (
          <div className={`message-${status.type}`} style={{marginTop: 10, fontSize: 14}}>
            {status.msg}
          </div>
        )}
      </form>
    </div>
  );
};

// --- Виджет Информации ---
const SubscriptionInfoCard = ({ user, isTrulyActive, formattedDate, squadName }) => {
  const { t } = useTranslation();
  const { currency } = useAuth();
  
  // Конвертируем баланс из USD в текущую выбранную валюту
  const displayBalance = useMemo(() => {
    const balanceUSD = user.balance_usd || user.balance || 0;
    return convertBalanceFromUSD(balanceUSD, currency || user.preferred_currency);
  }, [user.balance_usd, user.balance, currency, user.preferred_currency]);

  const daysLeft = useMemo(() => {
    if (!user.expireAt) return 0;
    const now = new Date();
    const expire = new Date(user.expireAt);
    const diffTime = expire - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays > 0 ? diffDays : 0;
  }, [user.expireAt]);

  return (
    <div className="glass-card info-card accent-card">
      <h3 className="client-widget-title">{t('dashboard.details_title')}</h3>
      <div className="info-list modern">
        <div className="info-item modern">
          <span className="info-label"><LuUser /> Email</span>
          <span className="info-value">{user.email}</span>
        </div>
        <div className="info-item modern">
          <span className="info-label"><LuActivity /> {t('dashboard.status')}</span>
          {isTrulyActive ? (
            <span className="status-chip success"><LuCircleCheck /> Активна</span>
          ) : (
            <span className="status-chip danger"><LuCircleX /> Не активна</span>
          )}
        </div>
        {isTrulyActive && (
          <>
            <div className="info-item modern">
              <span className="info-label"><LuClock /> {t('dashboard.expires_at')}</span>
              <span className="info-value">{formattedDate}</span>
            </div>
            <div className="info-item modern">
              <span className="info-label"><LuHourglass /> {t('dashboard.days_left', 'Осталось дней')}</span>
              <span className={`info-value ${daysLeft <= 3 ? 'text-warning' : ''}`}>
                {daysLeft}
              </span>
            </div>
          </>
        )}
        <div className="info-item modern">
          <span className="info-label"><LuShieldCheck /> Тариф</span>
          <span className="info-value">{squadName}</span>
        </div>
        <div className="info-item modern">
          <span className="info-label"><LuWallet /> Баланс</span>
          <span className="info-value" style={{fontWeight: 'bold', color: 'var(--accent-green)'}}>
            {displayBalance.toFixed(2)} {(currency || user.preferred_currency || 'uah').toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  );
};

// --- Виджет: Пополнение баланса ---
const BalanceCard = ({ user, onUpdateUser }) => {
  const { token, currency } = useAuth();
  
  // Конвертируем баланс из USD в текущую выбранную валюту
  const displayBalance = useMemo(() => {
    const balanceUSD = user.balance_usd || user.balance || 0;
    return convertBalanceFromUSD(balanceUSD, currency || user.preferred_currency);
  }, [user.balance_usd, user.balance, currency, user.preferred_currency]);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);

  useEffect(() => {
    // Загружаем доступные способы оплаты
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch('/api/public/available-payment-methods');
        if (response.ok) {
          const data = await response.json();
          setAvailablePaymentMethods(data.available_methods || []);
        }
      } catch (e) {
        console.error('Ошибка загрузки способов оплаты:', e);
      }
    };
    fetchPaymentMethods();
  }, []);

  const handleSubmitAmount = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      setError('Введите корректную сумму');
      return;
    }
    // Показываем модальное окно выбора способа оплаты
    setShowPaymentModal(true);
    setError(null);
  };

  const handleCreatePayment = async (paymentProvider) => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Введите корректную сумму');
      setShowPaymentModal(false);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setShowPaymentModal(false);

    try {
      // Создаем платеж для пополнения баланса
      const response = await fetch('/api/client/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tariff_id: null, // Для пополнения баланса тариф не нужен
          amount: parseFloat(amount),
          currency: user.preferred_currency || 'uah',
          payment_provider: paymentProvider,
          type: 'balance_topup'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Ошибка создания платежа');
      }

      if (data.payment_url) {
        // Перенаправляем на страницу оплаты
        window.location.href = data.payment_url;
      } else {
        throw new Error('Не получена ссылка на оплату');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <>
    <div className="glass-card balance-card accent-card">
      <h3 className="client-widget-title"><LuWallet /> Пополнение баланса</h3>
      <div className="balance-display" style={{marginBottom: '20px', padding: '15px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px'}}>
        <p style={{margin: 0, fontSize: '14px', color: 'var(--text-secondary)'}}>Текущий баланс</p>
        <p style={{margin: '5px 0 0 0', fontSize: '24px', fontWeight: 'bold', color: 'var(--accent-green)'}}>
          {displayBalance.toFixed(2)} {(currency || user.preferred_currency || 'uah').toUpperCase()}
        </p>
      </div>
      <form onSubmit={handleSubmitAmount}>
        <div style={{marginBottom: '15px'}}>
          <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)'}}>
            Сумма пополнения ({(currency || user.preferred_currency || 'uah').toUpperCase()})
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Введите сумму"
            style={{
              width: 'calc(100% - 4px)',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--glass-border)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '16px'
            }}
            required
          />
        </div>
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={loading}
          style={{width: '100%'}}
        >
          {loading ? 'Создание платежа...' : <><LuCreditCard /> Пополнить баланс</>}
        </button>
        {error && <div className="message-error" style={{marginTop: '15px'}}>{error}</div>}
        {success && <div className="message-success" style={{marginTop: '15px'}}>{success}</div>}
      </form>
    </div>

    {/* Модальное окно выбора способа оплаты */}
    {showPaymentModal && (
      <div 
        className="modal-overlay payment-modal-overlay" 
        onClick={() => setShowPaymentModal(false)}
      >
        <div 
          className="modal-content payment-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <h3>Выберите способ оплаты</h3>
          <div className="payment-methods-list">
            {availablePaymentMethods.includes('crystalpay') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('crystalpay')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'CrystalPay'}
              </button>
            )}
            {availablePaymentMethods.includes('heleket') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('heleket')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Heleket (Криптовалюты)'}
              </button>
            )}
            {availablePaymentMethods.includes('yookassa') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('yookassa')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'YooKassa (RUB)'}
              </button>
            )}
            {availablePaymentMethods.includes('yoomoney') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('yoomoney')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'YooMoney (RUB)'}
              </button>
            )}
            {availablePaymentMethods.includes('platega') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('platega')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Platega'}
              </button>
            )}
            {availablePaymentMethods.includes('platega_mir') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('platega_mir')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Карты МИР'}
              </button>
            )}
            {availablePaymentMethods.includes('mulenpay') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('mulenpay')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Mulenpay'}
              </button>
            )}
            {availablePaymentMethods.includes('urlpay') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('urlpay')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'UrlPay'}
              </button>
            )}
            {availablePaymentMethods.includes('telegram_stars') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('telegram_stars')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Telegram Stars ⭐'}
              </button>
            )}
            {availablePaymentMethods.includes('monobank') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('monobank')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Monobank 💳'}
              </button>
            )}
            {availablePaymentMethods.includes('btcpayserver') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('btcpayserver')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'BTCPayServer ₿ (Bitcoin)'}
              </button>
            )}
            {availablePaymentMethods.includes('tribute') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('tribute')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Tribute'}
              </button>
            )}
            {availablePaymentMethods.includes('robokassa') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('robokassa')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Robokassa'}
              </button>
            )}
            {availablePaymentMethods.includes('freekassa') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('freekassa')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Freekassa'}
              </button>
            )}
            {availablePaymentMethods.includes('kassa_ai') && (
              <button
                className="btn btn-primary"
                onClick={() => handleCreatePayment('kassa_ai')}
                disabled={loading}
              >
                {loading ? 'Создаем счёт…' : 'Kassa AI'}
              </button>
            )}
            {availablePaymentMethods.length === 0 && (
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-secondary)', 
                textAlign: 'center',
                padding: '20px'
              }}>
                Нет доступных способов оплаты. Обратитесь к администратору.
              </p>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

const TrafficUsageCard = ({ user }) => {
  const { t } = useTranslation();
  const formatBytes = (bytes, decimals = 2) => { if (!bytes) return '0 B'; const k = 1024; const i = Math.floor(Math.log(bytes) / Math.log(k)); return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i]; };
  const used = user.usedTrafficBytes || 0;
  const limit = user.trafficLimitBytes || 0; 
  const isUnlimited = limit === 0;
  const percentage = (!isUnlimited && used > 0) ? Math.min((used / limit) * 100, 100) : 0;

  return (
    <div className="glass-card info-card softer-card">
      <h3 className="client-widget-title">{t('dashboard.traffic_title')}</h3>
      {isUnlimited ? (
        <div className="traffic-stats unlimited"><span>Безлимит</span><LuActivity /></div>
      ) : (
        <>
          <div className="progress-bar-container"><div className="progress-bar-fill" style={{ width: `${percentage}%` }}></div></div>
          <div className="traffic-stats"><span>{formatBytes(used)}</span><span>{formatBytes(limit)}</span></div>
        </>
      )}
      <div className="info-list" style={{marginTop: '20px'}}>
         <div className="info-item">
            <span className="info-label"><LuDatabase /> Всего</span>
            <span className="info-value">{formatBytes(user.lifetimeUsedTrafficBytes || 0)}</span>
         </div>
      </div>
    </div>
  );
};

const QuickDownloadCard = ({ connectUrl, branding }) => {
  const { t } = useTranslation();
  
  // Если блок отключен в настройках - не показываем
  if (branding && branding.quick_download_enabled === false) {
    return null;
  }
  
  // Получаем deeplink схему из настроек или используем дефолтную
  const deeplinkScheme = branding?.quick_download_profile_deeplink || 'stealthnet://install-config?url=';
  const profileUrl = connectUrl ? `${deeplinkScheme}${encodeURIComponent(connectUrl)}` : null;
  
  // Получаем URL из настроек или используем пустые (кнопка не покажется)
  const windowsUrl = branding?.quick_download_windows_url || '';
  const androidUrl = branding?.quick_download_android_url || '';
  const macosUrl = branding?.quick_download_macos_url || '';
  const iosUrl = branding?.quick_download_ios_url || '';
  
  // Если нет ни одной ссылки и нет profileUrl - не показываем блок
  const hasAnyLink = windowsUrl || androidUrl || macosUrl || iosUrl || profileUrl;
  if (!hasAnyLink) {
    return null;
  }
  
  return (
    <section className="glass-card quick-download-section">
      <h3 className="client-widget-title">{t('dashboard.quick_download_title')}</h3>
      <div className="quick-download-buttons">
        {windowsUrl && (
        <a 
            href={windowsUrl} 
          className="btn btn-download btn-windows"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaWindows /> {t('dashboard.download_windows')}
        </a>
        )}
        {macosUrl && (
          <a 
            href={macosUrl} 
          className="btn btn-download btn-macos"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaApple /> {t('dashboard.download_macos')}
          </a>
        )}
        {androidUrl && (
        <a 
            href={androidUrl} 
          className="btn btn-download btn-android"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaAndroid /> {t('dashboard.download_android', 'Скачать для Android')}
        </a>
        )}
        {iosUrl && (
          <a 
            href={iosUrl} 
            className="btn btn-download btn-ios"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaApple /> {t('dashboard.download_ios', 'Скачать для iOS')}
          </a>
        )}
        {profileUrl ? (
          <a 
            href={profileUrl}
            className="btn btn-download btn-profile"
          >
            <LuPlus /> {t('dashboard.add_profile')}
          </a>
        ) : (
          <button 
            className="btn btn-download btn-profile"
            disabled
          >
            <LuPlus /> {t('dashboard.add_profile')}
          </button>
        )}
      </div>
    </section>
  );
};

const HeroCard = ({
  isTrulyActive,
  formattedDate,
  onUpgrade,
  onActivateTrial,
  trialLoading,
  daysLeft,
  connectUrl,
  onNavigateSupport,
  onNavigateReferrals
}) => {
  const { t } = useTranslation();
  const noSubscriptionLabel = t('dashboard.no_subscription', 'У вас нет подписки');

  return (
    <section className="hero-card glass-panel gradient-card">
      <div className="hero-container">
        <div className="hero-main">
          <p className="eyebrow-text"><LuSparkles /> {t('dashboard.hero_label', 'Ваш трафик под защитой')}</p>
          <h2>{isTrulyActive ? t('dashboard.hero_active', 'Подписка активна') : t('dashboard.hero_inactive', 'Нет активной подписки')}</h2>
          <p>{isTrulyActive ? t('dashboard.hero_active_desc', 'Мы удерживаем соединение стабильным и быстрым по всему миру.') : t('dashboard.hero_inactive_desc', 'Подключите тариф или активируйте триал, чтобы получить самый быстрый VPN.')}</p>
          <div className="hero-status-row">
            <span className={`status-dot ${isTrulyActive ? 'online' : 'offline'}`}></span>
            <span>{isTrulyActive ? `${t('dashboard.expires_at')} ${formattedDate}` : noSubscriptionLabel}</span>
          </div>
          <div className="quick-metrics">
            <div>
              <p>{t('dashboard.days_left', 'Осталось дней')}</p>
              <strong>{isTrulyActive ? daysLeft : '—'}</strong>
            </div>
          </div>
          <div className="hero-quick-actions">
            <button className="chip-btn" onClick={onUpgrade}>
              <LuShieldCheck /> {t('dashboard.hero_plans', 'Тарифы')}
            </button>
            <button className="chip-btn secondary" onClick={onNavigateSupport}>
              <LuLifeBuoy /> {t('dashboard.hero_support', 'Поддержка 24/7')}
            </button>
            <button className="chip-btn secondary" onClick={onNavigateReferrals}>
              <LuUsers /> {t('dashboard.hero_ref', 'Реферальная программа')}
            </button>
          </div>
        </div>
        <div className="hero-actions">
          {isTrulyActive ? (
            <>
              <button className="btn btn-primary hero-cta" type="button" onClick={onUpgrade}>
                {t('dashboard.hero_cta', 'Продлить подписку')} <LuArrowRight />
              </button>
              {connectUrl && (
                <div className="hero-connect-block">
                  <div className="hero-connect-info">
                    <span>{t('dashboard.connect_desc', 'Нажмите подключиться и следуйте инструкциям')}</span>
                  </div>
                  <button className="btn btn-connect hero-connect-btn" type="button" onClick={() => { if (connectUrl) window.location.href = connectUrl; }}>
                    <LuDownload /> {t('dashboard.connect_button', 'Подключиться')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <button className="btn btn-primary hero-cta" type="button" onClick={onUpgrade}>
              {t('dashboard.hero_plans', 'Выбрать тариф')} <LuArrowRight />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default function ClientStatusPage() {
  const { i18n } = useTranslation(); 
  const { user, setUser, token } = useAuth(); 
  const navigate = useNavigate();
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(null);

  // Загружаем branding настройки
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await fetch('/api/public/branding');
        if (res.ok) {
          const data = await res.json();
          setBranding(data);
        }
      } catch (e) {
        console.error('Ошибка загрузки branding:', e);
      }
    };
    fetchBranding();
  }, []);

  // Загружаем данные пользователя, если их еще нет
  useEffect(() => {
    // Если user уже есть, не загружаем
    if (user) {
      setLoading(false);
      return;
    }

    // Если токена нет, ждем
    if (!token) {
      setLoading(true);
      return;
    }

    // Флаг для отмены запроса при размонтировании
    let cancelled = false;

    // Загружаем данные пользователя
    const fetchUserData = async () => {
      setLoading(true);
      try {
        // Добавляем небольшую задержку для Telegram логина, чтобы токен успел установиться
        await new Promise(resolve => setTimeout(resolve, 50));
        
        if (cancelled) return;
        
        const response = await fetch('/api/client/me?force_refresh=true', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (cancelled) return;
        
        if (!response.ok) {
          if (response.status === 401) {
            // Если получили 401, возможно токен еще не активирован (например, после Telegram логина)
            // Пробуем еще раз через небольшую задержку
            await new Promise(resolve => setTimeout(resolve, 200));
            
            if (cancelled) return;
            
            const retryResponse = await fetch('/api/client/me?force_refresh=true', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!retryResponse.ok) {
              if (retryResponse.status === 401) {
                // Перенаправляем на логин если токен невалидный после повторной попытки
                navigate('/login');
                return;
              }
              throw new Error('Ошибка загрузки данных');
            }
            
            const retryData = await retryResponse.json();
            const retryUserData = retryData.response || retryData;
            
            if (cancelled) return;
            
            setUser(retryUserData);
            setLoading(false);
            return;
          }
          throw new Error('Ошибка загрузки данных');
        }
        const data = await response.json();
        const userData = data.response || data;
        
        if (cancelled) return;
        
        setUser(userData);
      } catch (err) {
        if (!cancelled) {
          console.error('Ошибка загрузки пользователя:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchUserData();
    
    // Очистка при размонтировании
    return () => {
      cancelled = true;
    };
  }, [token, user, setUser, navigate]);

  // Показываем загрузку пока данные не получены
  if (loading) {
    return (
      <main className="client-page-content modern">
        <div className="loading-full">Загрузка...</div>
      </main>
    );
  }

  // Если пользователь все еще не загружен, показываем сообщение
  if (!user) {
    return (
      <main className="client-page-content modern">
        <div className="loading-full">Нет данных пользователя.</div>
      </main>
    );
  }

  const isSubActive = user.activeInternalSquads?.length > 0;
  const isExpired = new Date(user.expireAt) < new Date();
  const isTrulyActive = isSubActive && !isExpired;
  const formattedDate = new Date(user.expireAt).toLocaleDateString(i18n.language);
  const squadName = isSubActive ? user.activeInternalSquads[0].name : "Нет";
  const daysLeft = (() => {
    if (!user.expireAt) return 0;
    const now = new Date();
    const expire = new Date(user.expireAt);
    const diffTime = expire - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays > 0 ? diffDays : 0;
  })();

  const handleActivateTrial = async () => {
    if (trialLoading) return;
    setTrialLoading(true);
    setTrialError(null);
    try {
      const response = await fetch('/api/client/activate-trial', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка активации');

      // Показываем успешное сообщение (если есть в ответе)
      if (data.message) {
        // Можно показать toast или просто обновить данные пользователя
        console.log('Trial activated:', data.message);
      }

      const meResponse = await fetch('/api/client/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (meResponse.ok) {
        const meData = await meResponse.json();
        setUser(meData.response || meData);
      } else {
        setUser((prev) => ({ ...prev, ...(data?.response || {}) }));
      }
      
      // Перезагружаем страницу для обновления UI
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      setTrialError(err.message);
    } finally {
      setTrialLoading(false);
    }
  };

  return (
    <main className="client-page-content modern">
      <HeroCard 
        isTrulyActive={isTrulyActive} 
        formattedDate={formattedDate}
        onUpgrade={() => navigate('/dashboard/tariffs')}
        onActivateTrial={handleActivateTrial}
        trialLoading={trialLoading}
        daysLeft={daysLeft}
        connectUrl={user.subscriptionUrl}
        onNavigateSupport={() => navigate('/dashboard/support')}
        onNavigateReferrals={() => navigate('/dashboard/referrals')}
      />

      <QuickDownloadCard connectUrl={user.subscriptionUrl} branding={branding} />

      {!isTrulyActive ? (
        <div className="client-grid no-subscription">
          <div className="grid-full">
            <TrialCard
              onActivateTrial={handleActivateTrial}
              loading={trialLoading}
              error={trialError}
            />
          </div>
        </div>
      ) : (
        <div className="client-grid">
          <SubscriptionInfoCard user={user} isTrulyActive={isTrulyActive} formattedDate={formattedDate} squadName={squadName} />
          <TrafficUsageCard user={user} />
          <BalanceCard user={user} onUpdateUser={(u) => setUser(u)} />
          <PromoCodeWidget onUpdateUser={(u) => setUser(u)} />
        </div>
      )}
    </main>
  );
}